import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler } from "@preveil-api";
import {
  DriveItemType, DocRenderTypes, FileSizeLimits, Message, MessageHandlerDisplayType, useAppDispatch, EntryItem, useDownloadFile, Account, AppConfiguration,
  useAppSelector, DOCS_PERMISSIONS_TYPES, DriveErrorMessages, MessageAnchors, buildNavigationLink, DriveEntryType, DriveUIActionTypes, mimeLookupVideo, DirectoryEntity, RootInfo,
  CollectionEntity, DefaultRoutes, Collection, BrowserType, decodeBmpToPng, Helpers
} from "src/common";
import { Icon, Loading } from "src/components";
import FileViewer from "./FileViewer.component";
import MediaGallery from "./MediaGallery.component";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  file_entry: EntryItem;
  current_account: Account;
  collection_info: CollectionEntity;
  root_info: RootInfo;
  current_directory: DirectoryEntity;
  media_entries: EntryItem[];
  handleAction: ActionHandlerFunction;
};

const is_app = AppConfiguration.buildForApp();
function FileDownloadAndViewComponent(props: AllProps) {
  const { file_entry, current_account, root_info, current_directory, collection_info, media_entries, handleAction } = props;
  const permissions = collection_info.permissions;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const gallery_index = useAppSelector((state: RootState) => state.drive.gallery_index);
  const file_to_view_name = useAppSelector((state: RootState) => state.drive.file_name);
  const current_link = useAppSelector((state: RootState) => state.drive.current_link);
  const file_type = !!file_entry.type_label ? file_entry.type_label : undefined;
  const [loading, setLoading] = useState<boolean>(true);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [view_only, setViewOnly] = useState<boolean>(false);
  const [file_to_view, setFileToView] = useState<File>();
  const [rendering_type, setRenderingType] = useState<string>();
  const is_media_type = file_entry.type_label === DriveItemType.Video || file_entry.type_label === DriveItemType.Image;
  const show_media_gallery = is_media_type && media_entries.length > 1;
  const video_ref = useRef<HTMLVideoElement | null>(null);
  const doc_container = useRef<HTMLDivElement>(null);
  const slide_in_ref = useRef<{ handlePageActions: ActionHandlerFunction }>();
  const { file, file_name, error, downloadFile, resetDownload } = useDownloadFile(current_account, is_app);

  // Description: On load - first check file size if type is video, otherwise start download
  useEffect(() => {
    isViewOnly();
    if (!show_media_gallery) {
      dispatch(driveActions.setFileName(file_entry.name));
      resetDownload();
      FileDownloadAndViewRequests.handleDownloadFile({ collection_id: file_entry.collection_id, file_id: file_entry.id, file_name: file_entry.name });
    }
  }, []);

  useEffect(() => {
    isViewOnly();
  }, [permissions]);

  // Description: handles what the downloadFileWeb hook returns (either an error or the file blob).
  useEffect(() => {
    !!file && handleDownloadSuccess(file);
    error && handleDownloadError();
  }, [file, error, file_name]);

  // Description: calls prepareFileReader once rendering type is set
  useEffect(() => {
    !!file_to_view && !show_media_gallery && prepareFileReader(file_to_view);
  }, [rendering_type]);

  // Description: sets Render Type once the downloaded file is set
  useEffect(() => {
    !!file_to_view && !!file_type && setRenderType(file_to_view, file_type);
  }, [file_to_view]);

  // Description: sets the view only state depending on permissions and location of file.
  function isViewOnly() {
    if (permissions?.global_local_sync === false) {
      // Global Local Sync takes precedence over other permissions.
      setViewOnly(true);
      return;
    }
    // discern if default collection, if so then show
    if (current_directory.collection_id === root_info.collection_id) {
      setViewOnly(false);
    } else {
      setViewOnly(permissions.type === DOCS_PERMISSIONS_TYPES.view_only);
    }
  }

  // Description: based on the file type, it sets the rendering type. This is used to prepare the file viewer. 
  // (if its of type viewer it will use pdftron)
  function setRenderType(downloaded_file: File, type: string): void {
    switch (type) {
      case DriveItemType.Word:
      case DriveItemType.Excel:
      case DriveItemType.PowerPoint:
      case DriveItemType.PDF:
        setRenderingType(DocRenderTypes.viewer);
        break;
      case DriveItemType.Image:
        if (
          downloaded_file.name.toLowerCase().endsWith(".tif") ||
          downloaded_file.name.toLowerCase().endsWith(".tiff")
        ) {
          setRenderingType(DocRenderTypes.viewer);
        } else {
          setRenderingType(DocRenderTypes.image);
        }
        break;
      case DriveItemType.Text:
      case DriveItemType.Code:
        setRenderingType(DocRenderTypes.text);
        break;
      case DriveItemType.Video:
        setRenderingType(DocRenderTypes.video);
        break;
      default:
        dispatch(
          uiActions.handleRequestErrors(
            new Message(
              DriveErrorMessages.invalid_document_type.replace(MessageAnchors.file_name, type),
              MessageHandlerDisplayType.logger,
            ),
          ),
        );
        handleAction({ actionType: DriveUIActionTypes.ResetFileEntry });
        break;
    }
  }

  // Description: If the rendering type is an image, video, or text, it prepares the file viewer using a doc_container and FileReader
  async function prepareFileReader(downloaded_file: File) {
    switch (rendering_type) {
      case DocRenderTypes.image:
        if (!!doc_container.current) {
          doc_container.current.innerHTML = "";
          const is_svg = downloaded_file.name.toLowerCase().endsWith(".svg");
          const is_bmp = downloaded_file.name.toLowerCase().endsWith(".bmp");
          if (is_svg) {
            // NOTE: SVG files must be rendered via <img> tag — CSS background-image
            //   restricts SVG rendering (viewBox, aspect ratio, complex SVGs fail).
            //   Using <img> is CSP-safe: browsers block scripts inside <img> SVGs.
            // NOTE: Re-wrap the blob with the correct MIME type. The download hook returns
            //   blobs typed as application/octet-stream, but browsers require image/svg+xml
            //   to render SVG content in <img> tags.
            const svg_blob = new Blob([downloaded_file], { type: "image/svg+xml" });
            const file_url = URL.createObjectURL(svg_blob);
            const img = document.createElement("img");
            img.src = file_url;
            img.alt = downloaded_file.name;
            doc_container.current.appendChild(img);
            doc_container.current.classList.add("image", "svg-image");
          } else {
            // NOTE: BITMAPV4/V5 extended-header BMPs (e.g. with embedded ICC profiles) cannot be
            // displayed by Safari via URL.createObjectURL — decodeBmpToPng converts them to PNG
            // via Canvas API. Returns null for standard BMPs so they fall back to the original blob.
            const is_safari = Helpers.getBrowser() === BrowserType.safari;
            const display_blob = (is_bmp && is_safari)
              ? (await decodeBmpToPng(await downloaded_file.arrayBuffer())) ?? downloaded_file
              : downloaded_file;
            const file_url = URL.createObjectURL(display_blob);
            doc_container.current.style.backgroundImage = `url(${file_url})`;
            doc_container.current.classList.add("image");
            // At a certain size, using image contain makes sense to make sure it fits
            if (downloaded_file.size > FileSizeLimits.DOC_VIEWER_IMAGE_SIZE_CAP) {
              doc_container.current.classList.add("large-image");
            }
          }
          doc_container.current.style.display = "flex";
          setLoading(false);
          show_media_gallery && slide_in_ref.current?.handlePageActions({ actionType: DriveUIActionTypes.SlideIn });
        }
        break;
      case DocRenderTypes.text:
        // using FileReader as its generally supported across browsers
        const file_reader = new FileReader();
        file_reader.onload = (e) => {
          // @ts-expect-error
          let text = e.target.result as string;
          if (!!text) {
            // format spaces and line endings
            text = text
              // replace line ends with <br>
              .replace(/\r|\v|\n/gm, "<br>")
              // replace multiple spaces with &nbsp;
              // but individual spaces can stay as that can negatively impact word wrapping
              .replace(/\s{2,}/gm, (spaces) => "&nbsp;".repeat(spaces.length));
          }
          if (!!doc_container.current) {
            doc_container.current.innerHTML = text;
            doc_container.current.classList.add("text");
          }
          setLoading(false);
        };
        file_reader.readAsText(downloaded_file);
        break;
      case DocRenderTypes.video:
        const video_url = URL.createObjectURL(downloaded_file);
        if (!!doc_container.current) {
          const video = document.createElement("video");
          doc_container.current.classList.add("video");
          video.controls = true;
          video.setAttribute("controlsList", "nodownload");
          video.autoplay = false;
          video.preload = "auto";
          const source = document.createElement("source");
          source.src = video_url;
          source.type = mimeLookupVideo(downloaded_file.name);
          video.appendChild(source);
          doc_container.current.appendChild(video);

          setLoading(false);
          video_ref.current = video;
          show_media_gallery && slide_in_ref.current?.handlePageActions({ actionType: DriveUIActionTypes.SlideIn, params: { video: video_ref.current } });
        }
        break;
    }
  }

  // Description: Converts a Blob Object to a File.
  function blobToFile(blob: Blob, modified_date: Date, name: string): File {
    // defining as any so we can add properties
    const updated_blob: any = blob;
    updated_blob.lastModified = modified_date;
    updated_blob.name = name;
    // Cast to a File() type
    return updated_blob as File;
  }

  // Description: (for both web and app) when there is an error on download - displays an error and resets the state.
  function handleDownloadError(msg?: any) {
    dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_downloading_file.replace(MessageAnchors.file_name, file_to_view_name || file_entry.name)), msg));
    reset();
    resetDownload();
  }

  // Description: (for both web and app) when successfully downloaded - checks if the user just wants to download or also view the file and 
  // handles it respectively
  function handleDownloadSuccess(blob: Blob) {
    const _file: File = blobToFile(blob, new Date(), file_to_view_name as string);
    setFileToView(_file);
    show_media_gallery && handleAction({ actionType: DriveUIActionTypes.SetFileEntry, params: media_entries[gallery_index] });
    !show_media_gallery && dispatch(driveActions.setLoading(false));
  }

  // Description: When the user closes the file viewer it navigates back to the directory
  // (since we are now using urls for files as well in v2), if the ids are equal to the root then navigate to the root path (/drive/root)
  function closeFileViewer() {
    reset();
    const { collection_id, id } = current_directory;
    // NOTES: If parent directory is ROOT (My PreVeil) then use custom url, if it is a LINK then use the link format url, else DIR
    (root_info.collection_id === collection_id && root_info.id === id) ?
      navigate(DefaultRoutes.drive_root) :
      (Collection.isRootChildNode(collection_info, root_info.id, id) && !!current_link) ?
        navigate(buildNavigationLink(current_link.collection_id, current_link.id, DriveEntryType.LINK)) :
        navigate(buildNavigationLink(collection_id, id, DriveEntryType.DIR));
  }

  // Description: Resets loading states, file entry, and gallery index
  function reset() {
    dispatch(driveActions.setLoading(false));
    setLoading(false);
    handleAction({ actionType: DriveUIActionTypes.ResetFileEntry });
    dispatch(driveActions.setGalleryIndex(-1));
  }

  // Description: Handle all children component actions and store it
  const FileDownloadAndViewRequests = {
    // Description: Download the file for viewing
    handleDownloadFile: (params: { collection_id: string, file_id: string, file_name: string }) => {
      downloadFile(params.collection_id, params.file_id, params.file_name, true);
    },
    // Description: Reset states related to file download
    handleResetDownload: () => {
      resetDownload();
    },
    // Description: Set render type of file
    handleSetRenderType: (params: { file: File, type: string }) => {
      setRenderType(params.file, params.type);
    },
    // Description: Prepares the file viewer using a doc_container or FileReader depending on file type
    handlePrepareFileReader: (file: File) => {
      prepareFileReader(file);
    },
    // Description: Set loading
    handleSetLoading: (loading: boolean) => {
      setLoading(loading);
    }
  };

  // Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((FileDownloadAndViewRequests as any)[callback] instanceof Function) {
      (FileDownloadAndViewRequests as any)[callback](action_obj.params);
    } else {
      // NOTE: If not found here propagate to parent
      handleAction(action_obj);
    }
  }

  return <Modal
    size="xl"
    centered
    show={!!file_to_view || show_media_gallery}
    fullscreen={!!fullscreen || undefined}
    className="file-viewer"
    dialogClassName={`${fullscreen ? "fullscreen" : ""}`}
    scrollable={true}
    onHide={() => closeFileViewer()}
    aria-label={file_to_view_name}>
    <Modal.Header closeButton className="ps-3 pe-3" closeVariant="white">
      <span className="title-span">
        <Modal.Title>{file_to_view_name}</Modal.Title>
        {show_media_gallery && (
          <div className="media-count">{Number(gallery_index) + 1} <span className="of">of</span> {media_entries.length}</div>
        )}
      </span>
      <Button
        variant="transparent"
        className="btn-icon ms-auto maximize"
        size="sm"
        onClick={() => setFullscreen(!fullscreen)}
      >
        <span>
          <Icon className={!!fullscreen ? "ficon-minimize-2" : "ficon-maximize-2"} />
        </span>
      </Button>
    </Modal.Header>
    <Modal.Body className={show_media_gallery ? "px-0" : ""} >
      {loading && <Loading message="Loading File" className="text-white"></Loading>}
      {show_media_gallery ? (
        <MediaGallery
          file_entry={file_entry}
          file_type={file_type}
          current_account={current_account}
          current_directory={current_directory}
          file_to_view={file_to_view}
          media_entries={media_entries}
          rendering_type={rendering_type}
          doc_container={doc_container}
          ref={slide_in_ref}
          view_only={view_only}
          handleAction={handlePageActions}
        />
      ) : (
        <div id="doc-outer-container">
          {!!file_to_view && rendering_type === DocRenderTypes.viewer && !!file_type && (
            <FileViewer
              file={file_to_view}
              type={file_type}
              is_view_only={view_only}
              handleAction={handlePageActions}
            />
          )}
          <div id="doc-container" ref={doc_container}></div>
        </div>
      )
      }
    </Modal.Body>
  </Modal>;
}

export default React.memo(FileDownloadAndViewComponent);
